import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../../../context/AuthContext";
import { getProfileByPatient as apiGetProfileByPatient } from "../../../../api/profile/create_profile";
import Table from "../../../../components/Table";
import Button from "../../../../components/Button";
import Modal from "../../../../components/Modal";
import styles from "../css/CreateProfile.module.css";
import modalStyles from "../../../../css/modal.module.css";
import Navbar from "../../../../components/navbar";
import Footer from "../../../../components/footer";
import FilterSearch from "../../../../components/FilterSearch(name)";
import FilterButtons from "../../../../components/universal-filter-tabs";
import Pagination from "../../../../components/Pagination";
import BeneficiariesModal from "../components/BeneficiariesModal.jsx";
import MedicalAidStep from "../components/MedicalAidStep.jsx";
import PersonalDetailsStep from "../components/PersonalDetailsStep.jsx";
import { fetchPractitionersFromServer, fetchAllPatientsAndPendingCreditsFromServer, handleSaveNewPatientHelper } from "../create_profile_functions";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../../../api";

export default function CreateProfile({ showAddButton = true, viewReadOnly = true }) {
  const { user } = useAuth();
  const roleId = Number(user?.roleid);
  const isDirector = roleId === 2;
  const location = useLocation();
  const navigate = useNavigate();

  const [showAddPatient, setShowAddPatient] = useState(false);
  const [modalStep, setModalStep] = useState(1);
  const [beneficiariesCount, setBeneficiariesCount] = useState(0);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [isViewMode, setIsViewMode] = useState(false);
  const [viewPersonal, setViewPersonal] = useState({});
  const [viewMedicalAid, setViewMedicalAid] = useState({});
  const [conditionsList, setConditionsList] = useState([""]);
  const [allergiesList, setAllergiesList] = useState([""]);
  const [modalMode, setModalMode] = useState("add");
  const [selectedPatientId, setSelectedPatientId] = useState(null);
  const [creditApplicationId, setCreditApplicationId] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [pendingSelectPracticeNumber, setPendingSelectPracticeNumber] = useState("");
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const pageSize = 6;
  const [totalCount, setTotalCount] = useState(0);
  const [errorModal, setErrorModal] = useState({ open: false, title: "", messages: [] });
  const [statusFilter, setStatusFilter] = useState("All");
  const handleBeneficiaryChange = (idx, updated) => { setBeneficiaries((prev) => prev.map((b, i) => (i === idx ? updated : b))); };

  const handleAddBeneficiary = () => {
    const baseAddress = {
      homeStreet: viewPersonal.homeStreet || "", homeSuburb: viewPersonal.homeSuburb || "", homeCity: viewPersonal.homeCity || "",
      homePostalCode: viewPersonal.homePostalCode || "", deliveryStreet: viewPersonal.deliveryStreet || "", deliverySuburb: viewPersonal.deliverySuburb || "",
      deliveryCity: viewPersonal.deliveryCity || "", deliveryPostalCode: viewPersonal.deliveryPostalCode || "",
    };
    setBeneficiaries((prev) => [
      ...prev,
      {
        ...baseAddress, enabled: true, allergiesList: [""],
      },
    ]);
  };

  const handleRemoveBeneficiary = (idx) => {
    setBeneficiaries((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleToggleBeneficiaryEnabled = (idx, enabling) => {
    setBeneficiaries((prev) =>
      prev.map((b, i) =>
        i === idx
          ? {
            ...b, enabled: enabling,
          }
          : b
      )
    );
  };

  const [tableRows, setTableRows] = useState([]);
  const doctorOptions = useMemo(() => doctors.map((d) => d.label), [doctors]);
  const doctorIdMap = useMemo(() => Object.fromEntries(doctors.map((d) => [d.label, d.id])), [doctors]);

  useEffect(() => {
    fetchPractitioners();
  }, []);

  async function fetchPractitioners() {
    try {
      const mapped = await fetchPractitionersFromServer();
      setDoctors(mapped);
    } catch (_) { }
  }

  useEffect(() => {
    if (!pendingSelectPracticeNumber) return;
    const match = doctors.find((d) => (d.practicenumber || "") === pendingSelectPracticeNumber);
    if (match) {
      setViewMedicalAid((prev) => ({ ...prev, practitionerid: match.id }));
      setPendingSelectPracticeNumber("");
    }
  }, [doctors, pendingSelectPracticeNumber]);

  const fetchAllPatients = async () => {
    try {
      const aggregated = await fetchAllPatientsAndPendingCreditsFromServer();
      setTableRows(aggregated);
      setTotalCount(aggregated.length);
    } catch (_) {
      setTableRows([]);
      setTotalCount(0);
    }
  };

  useEffect(() => {
    fetchAllPatients();
  }, []);

  const handleRefreshPatientsTable = () => {
    setShowAddPatient(false);

    setIsViewMode(false);
    setSelectedPatientId(null);
    setCurrentPage(1);
    fetchAllPatients();
  };

  useEffect(() => {
    if (location?.state?.openAdd) {
      setIsViewMode(false);
      setViewPersonal({});
      setViewMedicalAid({});
      setBeneficiaries([]);
      setBeneficiariesCount(0);
      setConditionsList([""]);
      setAllergiesList([""]);
      setModalStep(1);
      setModalMode("add");
      setShowAddPatient(true);
    }
  }, [location?.state?.openAdd]);

  const DRAFT_KEY = "createProfileDraft";
  const saveDraftToSession = (extra = {}) => {
    try {
      const payload = {
        modalStep, beneficiariesCount, beneficiaries, isViewMode, viewPersonal, viewMedicalAid, conditionsList,
        allergiesList, modalMode, showAddPatient, ...extra,
      };
      sessionStorage.setItem(DRAFT_KEY, JSON.stringify(payload));
    } catch (_) { }
  };
  const loadDraftFromSession = () => {
    try {
      const raw = sessionStorage.getItem(DRAFT_KEY);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (_) {
      return null;
    }
  };
  const clearDraftFromSession = () => {
    try {
      sessionStorage.removeItem(DRAFT_KEY);
    } catch (_) { }
  };

  useEffect(() => {
    if (location?.state?.restoreCreateDraft) {
      const draft = loadDraftFromSession();
      if (draft) {
        setModalStep(draft.modalStep ?? 2);
        setBeneficiariesCount(draft.beneficiariesCount ?? 0);
        setBeneficiaries(Array.isArray(draft.beneficiaries) ? draft.beneficiaries : []);
        setIsViewMode(!!draft.isViewMode);
        setViewPersonal(draft.viewPersonal || {});
        setViewMedicalAid(draft.viewMedicalAid || {});
        setConditionsList(Array.isArray(draft.conditionsList) ? draft.conditionsList : [""]);
        setAllergiesList(Array.isArray(draft.allergiesList) ? draft.allergiesList : [""]);
        setModalMode(draft.modalMode || "add");
        setShowAddPatient(true);
        const navPractice = location?.state?.selectPracticeNumber;
        const selPN = navPractice || draft.selectPracticeNumber;
        if (selPN) setPendingSelectPracticeNumber(selPN);
      }
      clearDraftFromSession();
      navigate(location.pathname, { replace: true });
    }
  }, [location?.state?.restoreCreateDraft]);

  const handleNavigateAddDoctor = () => {
    if (isDirector) return;
    saveDraftToSession();
    navigate("/Profiles/add-doctor", { state: { fromCreateProfile: true } });
  };

  const filteredRows = useMemo(
    () => {
      const q = (search || "").toLowerCase().trim();

      const bySearch = (tableRows || []).filter((row) => {
        if (!q) return true;
        const surname = (row.surname || "").toLowerCase();
        const firstname = (row.firstname || "").toLowerCase();
        const initial = (row.initial || "").toLowerCase();
        const title = (row.title || "").toLowerCase();
        const address = (row.address || "").toLowerCase();
        return (
          surname.includes(q) ||
          firstname.includes(q) ||
          initial.includes(q) ||
          title.includes(q) ||
          address.includes(q)
        );
      });

      return bySearch.filter((row) => {
        const hasProfile = !!row?.profile;
        const enabled = hasProfile ? row.profile.enabled !== false : true;
        if (statusFilter === "Active") return hasProfile && enabled;
        if (statusFilter === "Inactive") return hasProfile && !enabled;
        if (statusFilter === "Incomplete") return !hasProfile;
        return true;
      });
    },
    [tableRows, search, statusFilter]
  );

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));

  const pageData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);

  const renderCell = (value, row) => {
    const enabled = row?.profile ? row.profile.enabled !== false : true;
    const className = enabled ? undefined : styles.disabledCellText;
    return <span className={className}>{value}</span>;
  };

  const columns = useMemo(
    () => [
      { header: "Surname", accessor: "surname", render: (value, row) => renderCell(value, row) }, { header: "Firstname", accessor: "firstname", render: (value, row) => renderCell(value, row) },
      { header: "Initial", accessor: "initial", render: (value, row) => renderCell(value, row) }, { header: "Title", accessor: "title", render: (value, row) => renderCell(value, row) },
      { header: "Address", accessor: "address", render: (value, row) => renderCell(value, row) },
      {
        header: "Status",
        accessor: "status",
        render: (_value, row) => {
          const enabled = row?.profile ? row.profile.enabled !== false : true;
          const className = enabled ? undefined : styles.disabledCellText;
          return <span className={className}>{enabled ? "Enabled" : "Disabled"}</span>;
        },
      },
      {
        header: "Action",
        accessor: "action",
        render: (_value, row) => (
          <Button
            variant="primary"
            onClick={async () => {
              const id = row?.id;
              if (!id) return;
              try {
                const isCreditApp = String(id).startsWith("ca-") || row?._type === "creditapplication";
                if (isCreditApp) {
                  const applicationId = row?.applicationId || Number(String(id).replace(/^ca-/, ""));
                  const res = await api.get(`/api/public/credit-applications/${applicationId}`);
                  const application = res?.data?.application;
                  if (!application) throw new Error("Failed to fetch credit application");

                  const normalizedDob = application.dob
                    ? new Date(application.dob).toISOString().slice(0, 10)
                    : "";

                  const idNumberRaw = String(application.idNumber || "").trim();
                  const idType = /^\d{13}$/.test(idNumberRaw) ? "id" : "passport";

                  const vp = {
                    surname: application.lastName || "", firstNames: application.firstName || "", title: "",
                    idNumber: idNumberRaw,
                    idType,
                    dob: normalizedDob,
                    sex: application.gender || "",
                    age: "",
                    weight: "",
                    homeStreet: application.streetAddress || "",
                    homeSuburb: application.suburb || "",
                    homeCity: application.city || "",
                    homePostalCode: application.postalCode || "",
                    deliveryStreet: application.streetAddress || "",
                    deliverySuburb: application.suburb || "",
                    deliveryCity: application.city || "",
                    deliveryPostalCode: application.postalCode || "",
                    accountNumber: "",
                    profileNumber: "",
                    profileEnabled: true,
                    email: application.email || "",
                    cell: String(application.phone || ""),
                    employer: application.employerName || "",
                    medicalaidprovider: "",
                    practitionerid: "",
                    dependentCode: "",
                    note: "",
                    conditions: "",
                  };

                  const vma = {
                    provider: "",
                    medicalAidNumber: "",
                    optioncodeid: "",
                    practitionerid: "",
                    dependentCode: "",
                    note: "",
                  };

                  setConditionsList([""]);
                  setAllergiesList([""]);
                  setIsViewMode(!!viewReadOnly);
                  setViewPersonal(vp);
                  setViewMedicalAid(vma);
                  setBeneficiaries([]);
                  setBeneficiariesCount(0);
                  setModalStep(1);
                  setModalMode(viewReadOnly ? "view" : "add");
                  setSelectedPatientId(null);
                  setCreditApplicationId(applicationId);
                  setShowAddPatient(true);
                  return;
                }

                setSelectedPatientId(id);
                const data = await apiGetProfileByPatient(id);
                if (!data || data.error) throw new Error(data?.error || "Failed to fetch profile");

                const main = data?.main || {};
                const mm = data?.mainmember || {};
                const profile = data?.profile || {};

                const bens = Array.isArray(data?.beneficiaries) ? data.beneficiaries : [];

                const homeParts = String(main.homeaddress || "")
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean);
                const d = main.deliveryaddress;
                let d1 = { street: "", suburb: "", city: "", postal: "" };
                if (d && typeof d === "object" && !Array.isArray(d) && d["Delivery Address 1"]) {
                  const obj = d["Delivery Address 1"] || {};
                  d1 = {
                    street: obj.street || "",
                    suburb: obj.suburb || "",
                    city: obj.city || "",
                    postalCode: obj.postalCode || obj.postal || "",
                  };
                } else {
                  const raw = Array.isArray(d) ? (d[0] || "") : (d || "");
                  const parts = String(raw).split(",").map((s) => s.trim()).filter(Boolean);
                  d1 = { street: parts[0] || "", suburb: parts[1] || "", city: parts[2] || "", postal: parts[3] || "" };
                }

                const mainIdType = /^\d{13}$/.test(String(main.idnumber || "").trim()) ? "id" : "passport";

                const vp = {
                  surname: main.surname || "", firstNames: main.firstname || "", title: main.title || "",
                  idNumber: mainIdType === "id" ? (main.idnumber || "") : (main.passportnumber || ""),
                  idType: mainIdType, dob: main.dateofbirth ? String(main.dateofbirth).slice(0, 10) : "",
                  sex: main.sex || "", age: main.age ?? "", weight: main.weightinkg ?? "",
                  homeStreet: homeParts[0] || "", homeSuburb: homeParts[1] || "", homeCity: homeParts[2] || "", homePostalCode: homeParts[3] || "",
                  deliveryStreet: d1.street || "", deliverySuburb: d1.suburb || "", deliveryCity: d1.city || "", deliveryPostalCode: d1.postalCode || d1.postal || "",
                  accountNumber: mm.accountnumber || "", profileNumber: profile.profilenumber || "", profileEnabled: profile.enabled !== false,
                  email: main.email || "", cell: main.contactnumber || "", employer: mm.employer || "", medicalaidprovider: mm.medicalaidprovider || "",
                  practitionerid: mm.practitionerid || "", dependentCode: main.dependentcode || "", note: main.note || "",
                  conditions: (Array.isArray(main.chronicconditions) ? main.chronicconditions : []).join(", "),
                };

                const vma = {
                  provider: mm.medicalaidprovider || "", medicalAidNumber: mm.medicalaidnumber || "", practitionerid: mm.practitionerid || "",
                  optioncodeid: mm.optioncodeid ?? "",
                  patientid: main.patientid ?? null,
                  dependentCode: main.dependentcode || "", note: main.note || "",
                };

                setConditionsList(Array.isArray(main.chronicconditions) && main.chronicconditions.length ? [...main.chronicconditions] : [""]);
                setAllergiesList(Array.isArray(main.allergies) && main.allergies.length ? [...main.allergies] : [""]);

                const bensMapped = bens.map((b) => {
                  const hParts = String(b.homeaddress || "").split(",").map((s) => s.trim()).filter(Boolean);
                  const bd = b.deliveryaddress;
                  let bd1 = { street: "", suburb: "", city: "", postal: "" };
                  let extras = [];
                  if (bd && typeof bd === "object" && !Array.isArray(bd) && bd["Delivery Address 1"]) {
                    const keys = Object.keys(bd).sort();
                    keys.forEach((k, i) => {
                      const obj = bd[k] || {};
                      const payload = { street: obj.street || "", suburb: obj.suburb || "", city: obj.city || "", postalCode: obj.postalCode || obj.postal || "" };
                      if (i === 0) bd1 = { street: payload.street, suburb: payload.suburb, city: payload.city, postal: payload.postalCode };
                      else extras.push(payload);
                    });
                  } else {
                    const raw = Array.isArray(bd) ? (bd[0] || "") : (bd || "");
                    const parts = String(raw).split(",").map((s) => s.trim()).filter(Boolean);
                    bd1 = { street: parts[0] || "", suburb: parts[1] || "", city: parts[2] || "", postal: parts[3] || "" };
                  }

                  const benIdType = /^\d{13}$/.test(String(b.idnumber || "").trim()) ? "id" : "passport";

                  return {
                    surname: b.surname || "", firstNames: b.firstname || "", initial: b.initial || "", title: b.title || "",
                    idNumber: benIdType === "id" ? (b.idnumber || "") : (b.passportnumber || ""),
                    idType: benIdType, dob: b.dateofbirth ? String(b.dateofbirth).slice(0, 10) : "",
                    sex: b.sex || "", age: b.age ?? "", weight: b.weightinkg ?? "", cell: b.contactnumber || "",
                    dependentCode: b.dependentcode || "", conditions: Array.isArray(b.chronicconditions) && b.chronicconditions.length ? (b.chronicconditions[0] || "") : "",
                    conditionsExtra: Array.isArray(b.chronicconditions) ? (b.chronicconditions.slice(1) || []) : [],
                    allergiesList: Array.isArray(b.allergies) && b.allergies.length ? [...b.allergies] : [""],
                    homeStreet: hParts[0] || "", homeSuburb: hParts[1] || "", homeCity: hParts[2] || "", homePostalCode: hParts[3] || "",
                    deliveryStreet: bd1.street || "", deliverySuburb: bd1.suburb || "", deliveryCity: bd1.city || "", deliveryPostalCode: bd1.postal || "",
                    deliveryAddressesExtra: extras, enabled: b.enabledbeneficiary !== false,
                  };
                });

                const enabledBenCount = bensMapped.filter((b) => b && b.enabled !== false).length;

                setIsViewMode(!!viewReadOnly);
                setViewPersonal(vp);
                setViewMedicalAid(vma);
                setBeneficiaries(bensMapped);
                setBeneficiariesCount(enabledBenCount);
                setModalStep(1);
                setModalMode(viewReadOnly ? "view" : "update");
                setSelectedPatientId(id);
                setCreditApplicationId(null);
                setShowAddPatient(true);
              } catch (e) {
              }
            }}
            className={styles.viewBtn}
          >
            {viewReadOnly ? "View" : "Update"}
          </Button>
        ),
      },
    ],
    []
  );

  const showError = (title, messages) => {
    const list = Array.isArray(messages) ? messages : [messages];
    setErrorModal({ open: true, title, messages: list.filter(Boolean) });
  };

  async function handleSaveNewPatient() {
    await handleSaveNewPatientHelper({
      isDirector, viewPersonal, viewMedicalAid, beneficiaries, conditionsList, allergiesList, modalMode,
      selectedPatientId, beneficiariesCount, creditApplicationId, setShowAddPatient, setIsViewMode, setSelectedPatientId,
      setCurrentPage, fetchAllPatients, showError,
    });
  }

  return (
    <div className={styles.pageWrapper}>
      <Navbar />
      <div className={styles.toolbarRow}>
        <div className={styles.filterCard}>
          <FilterSearch
            filterName="Filter"
            value={search}
            onChange={setSearch}
            placeholder="Search Name"
          />
          <FilterButtons
            buttons={["All", "Active", "Inactive", "Incomplete"]}
            activeButton={statusFilter}
            onButtonClick={setStatusFilter}
          />
        </div>

        {showAddButton && !isDirector && (
          <Button
            variant="primary"
            onClick={() => {
              setIsViewMode(false);
              setViewPersonal({});
              setViewMedicalAid({});
              setBeneficiaries([]);
              setBeneficiariesCount(0);
              setConditionsList([""]);
              setAllergiesList([""]);
              setModalStep(1);
              setModalMode("add");
              setShowAddPatient(true);
            }}
            className={styles.addBtn}
          >
            Create
          </Button>
        )}
      </div>

      <div className={styles.tableSection}>
        <Table columns={columns} data={pageData} />
      </div>

      <div className={styles.paginationContainer}>
        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
      </div>

      {showAddPatient && modalStep !== 3 && (
        <div className={modalStyles.modalPopup}>
          <div className={`${modalStyles.modalContent} ${modalStyles.modalContentNarrow}`}>
            <div className={styles.modalCard}>
              {modalStep === 1 ? (
                <PersonalDetailsStep
                  isViewMode={isViewMode} viewPersonal={viewPersonal} setViewPersonal={setViewPersonal}
                  beneficiaries={beneficiaries} beneficiariesCount={beneficiariesCount}
                  setBeneficiariesCount={setBeneficiariesCount} setBeneficiaries={setBeneficiaries}
                  conditionsList={conditionsList} setConditionsList={setConditionsList}
                  allergiesList={allergiesList} setAllergiesList={setAllergiesList}
                  setModalStep={setModalStep} modalMode={modalMode} setShowAddPatient={setShowAddPatient}
                  setIsViewMode={setIsViewMode} selectedPatientId={selectedPatientId} onProfileEnabledChanged={handleRefreshPatientsTable}
                  onValidationError={showError}
                />
              ) : modalStep === 2 ? (
                <MedicalAidStep
                  isViewMode={isViewMode} viewMedicalAid={viewMedicalAid} setViewMedicalAid={setViewMedicalAid}
                  viewPersonal={viewPersonal} beneficiariesCount={beneficiariesCount} setBeneficiariesCount={setBeneficiariesCount}
                  beneficiaries={beneficiaries} setBeneficiaries={setBeneficiaries} setModalStep={setModalStep}
                  doctorOptions={doctorOptions} doctorIdMap={doctorIdMap} setShowAddPatient={setShowAddPatient}
                  doctors={doctors} refreshDoctors={fetchPractitioners} onNavigateAddDoctor={handleNavigateAddDoctor}
                  canAddPractitioner={!isDirector} onValidationError={showError}
                />
              ) : null}
            </div>
          </div>
        </div>
      )}

      {modalStep === 3 && (
        <BeneficiariesModal
          showAddPatient={showAddPatient}
          setShowAddPatient={setShowAddPatient}
          beneficiaries={beneficiaries}
          modalMode={modalMode}
          viewReadOnly={viewReadOnly}
          mainHome={{
            homeStreet: viewPersonal.homeStreet || "", homeSuburb: viewPersonal.homeSuburb || "", homeCity: viewPersonal.homeCity || "",
            homePostalCode: viewPersonal.homePostalCode || "",
          }}
          mainDelivery={{
            deliveryStreet: viewPersonal.deliveryStreet || "", deliverySuburb: viewPersonal.deliverySuburb || "", deliveryCity: viewPersonal.deliveryCity || "",
            deliveryPostalCode: viewPersonal.deliveryPostalCode || "",
          }}
          handleAddBeneficiary={handleAddBeneficiary} handleBeneficiaryChange={handleBeneficiaryChange} handleRemoveBeneficiary={handleRemoveBeneficiary}
          handleSaveNewPatient={handleSaveNewPatient} onToggleBeneficiaryEnabled={handleToggleBeneficiaryEnabled}
          setModalStep={setModalStep}
        />
      )}

      {errorModal.open && (
        <Modal onClose={() => setErrorModal({ open: false, title: "", messages: [] })} closeStyle="plain">
          <h2>{errorModal.title || "Validation error"}</h2>
          {errorModal.messages && errorModal.messages.length > 0 && (
            <ul>
              {errorModal.messages.map((msg, idx) => (
                <li key={idx}>{msg}</li>
              ))}
            </ul>
          )}
        </Modal>
      )}

      <Footer />
    </div>
  );
}